import React, { useState, useEffect } from 'react';
import { Card, CardBody, Row, Col, Modal, Button } from 'react-bootstrap';
import DataTable, { TableColumn } from "react-data-table-component";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSave, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import ButtonComponent from '../../../components/ButtonComponent';
import ButtonComponentV2 from '../../../components/ButtonComponentV2';
import ControlledTextField from '../../../components/ControlledTextField';
import EditButtonComponent from '../../../components/EditButtonComponent';
import TableLoadingComponent from '../../../components/TableLoadingComponent';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Swal from "sweetalert2";
import {
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetAllRolesQuery,
  RoleItem
} from '../../../redux/services/rolesPermissionService';

interface RoleFormData {
  displayName: string;
  description: string;
}

const Roles = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleItem | null>(null);
  const [apiRoles, setApiRoles] = useState<any[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deletingRoleId, setDeletingRoleId] = useState<string | null>(null);

  const [createRole, { isLoading: isCreating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();
  const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation();
  const { data: rolesData, isFetching, refetch: refetchRoles } = useGetAllRolesQuery();

  const isLoading = isCreating || isUpdating || isDeleting || isFetching;

  const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm<RoleFormData>();

  // Sync local state from cached query
  useEffect(() => {
    if (rolesData) {
      const normalizedRoles = rolesData.map((role: any) => ({
        ...role,
        isActive: role.isActive !== undefined ? role.isActive : true,
      }));
      setApiRoles(normalizedRoles);
    }
  }, [rolesData]);

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
  };

  const handleAddRole = () => {
    setEditingRole(null);
    reset();
    setShowModal(true);
  };

  const handleEditRole = (role: RoleItem) => {
    setEditingRole(role);
    setValue('displayName', role.displayName);
    setValue('description', role.description);
    setShowModal(true);
  };

  const handleDeleteRole = async (roleId: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#590B21",
      cancelButtonColor: "#ddd",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setDeletingRoleId(roleId);
        try {
          await deleteRole({
            roleId: roleId
          }).unwrap();

          // Remove from local state after successful deletion
          setApiRoles(apiRoles.filter(role => role.id !== roleId));
          Swal.fire("Deleted!", "Role has been deleted.", "success");
          setDeletingRoleId(null);
        } catch (error) {
          console.error('Error deleting role:', error);
          Swal.fire(
            "Error!",
            "An error occurred while deleting the role.",
            "error"
          );
          setDeletingRoleId(null);
        }
      }
    });
  };

  const onSubmit = async (data: RoleFormData) => {
    try {
      if (editingRole) {
        // Update existing role using RTK mutation
        await updateRole({
          id: editingRole.id,
          displayName: data.displayName,
          description: data.description
        }).unwrap();

        await refetchRoles();
        toast.success('Role updated successfully');
        setShowModal(false);
      } else {
        // Create new role using RTK mutation
        await createRole({
          displayName: data.displayName,
          description: data.description
        }).unwrap();

        await refetchRoles();
        toast.success('Role created successfully');
        setShowModal(false);
      }
      setShowModal(false);
      reset();
    } catch (error: any) {
      console.error('Error:', error);
      if (error.status === 409) {
        toast.error(error.data)
      }
      else {
        toast.error('Something went wrong');
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    reset();
    setEditingRole(null);
  };

  const columns: TableColumn<RoleItem>[] = [
    {
      name: <h6 className="fw-bold">#</h6>,
      cell: (row, index) => <span>{index + 1}</span>,
      sortable: false,
      width: '80px',
    },
    {
      name: <h6 className="fw-bold">Role Name</h6>,
      selector: (row) => row.displayName,
      cell: (row) => <strong>{row.displayName}</strong>,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Description</h6>,
      selector: (row) => row.description,
      cell: (row) => <span>{row.description}</span>,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Status</h6>,
      cell: (row) => (
        <span className={`badge ${row.isActive ? 'bg-success' : 'bg-secondary'}`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
      sortable: false,
    },
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      cell: (row) => {
        const createdDate = row.createdAt?._seconds
          ? new Date(row.createdAt._seconds * 1000).toLocaleDateString()
          : 'N/A';
        return <span>{createdDate}</span>;
      },
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Actions</h6>,
      cell: (row) => (
        <div className="d-flex gap-2">
          <EditButtonComponent
            onClick={() => handleEditRole(row)}
          />
          {deletingRoleId === row.id ? (
            <Button variant="light" disabled={true}>
              <FontAwesomeIcon icon={faSpinner} spin />
            </Button>
          ) : (
            <Button variant="light" onClick={() => handleDeleteRole(row.id)}>
              <FontAwesomeIcon icon={faTrash} title="Delete" color="#F24E1E" />
            </Button>
          )}
        </div>
      ),
      sortable: false,
      right: true,
    },
  ];

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center px-4">
            <h4>Role Management</h4>
            <ButtonComponentV2
              titel="Add Role"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={handleAddRole}
              color="#fff"
              backgroundColor="#590B21"
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <CardBody>
              {isFetching ? (<TableLoadingComponent numColumns={4} />) : (
                <DataTable
                  columns={columns}
                  data={apiRoles}
                  responsive
                  pagination
                  highlightOnHover
                  pointerOnHover
                  paginationPerPage={rowsPerPage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  noDataComponent={<div>No roles found. Click "Add Role" to create one.</div>}
                  progressPending={isFetching}
                  progressComponent={<div><TableLoadingComponent numColumns={5} /></div>}
                />)}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Role Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingRole ? 'Edit Role' : 'Add New Role'}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <Row className="mb-3">
              <Col>
                <ControlledTextField
                  label="Role Name"
                  placeholder="Enter role name"
                  inputName="displayName"
                  inputType="text"
                  control={control}
                  rules={{
                    required: 'Role name is required',
                    minLength: {
                      value: 2,
                      message: 'Role name must be at least 2 characters'
                    }
                  }}
                />
                {errors.displayName && (
                  <div className="text-danger mt-1">{errors.displayName.message}</div>
                )}
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <ControlledTextField
                  label="Description"
                  placeholder="Enter role description"
                  inputName="description"
                  inputType="textarea"
                  control={control}
                  rules={{
                    required: 'Description is required',
                    minLength: {
                      value: 5,
                      message: 'Description must be at least 5 characters'
                    }
                  }}
                />
                {errors.description && (
                  <div className="text-danger mt-1">{errors.description.message}</div>
                )}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <ButtonComponent
              titel="Cancel"
              icon={<FontAwesomeIcon icon={faTimes} />}
              onClick={handleCloseModal}
              isDisabled={isLoading}
            />
            <ButtonComponent
              titel={isLoading ? "Saving..." : "Save"}
              icon={<FontAwesomeIcon icon={faSave} />}
              type="submit"
              isDisabled={isLoading}
            />
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default Roles;
